// Internal Team Chat — Phases 1-3 + Pro pack (reactions, threads, search,
// presence, web-push).
//
// Two-pane layout: left = conversation list (topic channels + 1-to-1 DMs),
// right = the active conversation. Message bodies are encrypted at rest on
// the backend and pushed live via SSE. DM frames reach only the two
// participants. Dates render en-US (feedback_date_format_always_en_us).
//
//   - "general"  : the always-present agency channel.
//   - topic      : channels created by the team (#sales, #renewals).
//   - dm:<a>:<b> : private 1-to-1, key sorted so the pair is stable.
//
// Pro pack:
//   - Reactions : emoji chips under a bubble; toggle via POST /reactions,
//                 converge via the chat.message.reacted SSE event.
//   - Threads   : Slack-style one-level replies. Top-level lists exclude
//                 replies; a parent shows "N respuestas" opening the panel.
//   - Search    : server decrypts + matches (accent/case-insensitive);
//                 clicking a hit jumps to its channel/thread.
//   - Presence  : green dot for teammates with a live SSE connection.
//   - Push      : bell toggle registers this browser for Web Push so chat
//                 pings arrive even with Deskte closed (no PHI in payload).

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MessageSquare, Send, Loader2, RefreshCw, Plus, Hash, Users, X, Paperclip, FileText,
  Pencil, Trash2, Check, Search, Bell, BellOff, ArrowLeft, Reply, SmilePlus,
  Settings,
} from 'lucide-react';
import { Modal } from './ui.jsx';
import { BlobImg, BlobLink } from './BlobMedia.jsx';
import {
  listChatMessages, sendChatMessage, markChatRead,
  listChatChannels, createChatChannel, archiveChatChannel, resolveDmChannel, listUsers, listChatReadStates,
  editChatMessage, deleteChatMessage, sendChatTyping,
  toggleChatReaction, getChatThread, searchChatMessages, getChatPresence,
  getPushPublicKey, subscribePush, unsubscribePush,
  getChatEventRules, saveChatEventRules,
} from '../api.js';

const PAGE = 50;
const GENERAL = 'general';
const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🎉', '✅'];

// Automatic feed events (must mirror CHAT_FEED_EVENTS in the backend).
// Label pairs are [es, en].
const FEED_EVENT_DEFS = [
  { event: 'lead.created', icon: '📥', es: 'Nuevo lead', en: 'New lead' },
  { event: 'quote.viewed', icon: '👀', es: 'Cliente abre una cotización', en: 'Client opens a quote' },
  { event: 'quote.accepted', icon: '✅', es: 'Cotización aceptada', en: 'Quote accepted' },
  { event: 'quote.declined', icon: '🚫', es: 'Cotización rechazada', en: 'Quote declined' },
  { event: 'signature.signed', icon: '✍️', es: 'Documento firmado', en: 'Document signed' },
  { event: 'portal.doc.uploaded', icon: '📎', es: 'Cliente sube documento al portal', en: 'Client uploads a document' },
  { event: 'policy.created', icon: '🛡️', es: 'Póliza creada', en: 'Policy created' },
  // NO es un aviso: es el interruptor del reparto por ramo (Lissette
  // 2026-09-14). Va debajo de "Póliza creada" y sin desplegable de canal — el
  // canal lo decide la póliza, no la configuración.
  {
    event: 'policy.created.por_ramo', icon: '🗂️', debajoDe: 'policy.created',
    es: 'Cada ramo a su canal: "Pólizas: Salud", "Pólizas: Vida"… Se crean solos la primera vez.',
    en: 'Each line of business in its own channel: "Pólizas: Salud", "Pólizas: Vida"… created on first use.',
  },
  { event: 'policy.status_changed', icon: '🔄', es: 'Cambio de estatus de póliza', en: 'Policy status change' },
  { event: 'payment.marked', icon: '💵', es: 'Pago registrado', en: 'Payment marked paid' },
  { event: 'task.completed', icon: '✔️', es: 'Tarea completada', en: 'Task completed' },
  { event: 'appointment.booked', icon: '📅', es: 'Cita agendada (link público)', en: 'Appointment booked' },
  { event: 'client.life_change', icon: '🔄', es: 'Cambio de vida (nota en el cliente)', en: 'Life change (client note)' },
  { event: 'client.created', icon: '👤', es: 'Cliente nuevo', en: 'New client' },
  { event: 'task.created', icon: '📝', es: 'Tarea nueva', en: 'New task' },
  { event: 'ticket.created', icon: '🎫', es: 'Ticket nuevo', en: 'New ticket' },
  { event: 'lead.referral', icon: '🤝', es: 'Referido nuevo (lead con fuente "referido")', en: 'New referral (lead source says "referral")' },
  // Auto-routed by default to the auto-created channels "Prospectos y
  // Referidos" / "Clientes nuevos" (Lissette 2026-07-15).
  { event: 'client.prospect', icon: '🌱', es: 'Prospecto/Referido nuevo (cliente)', en: 'New prospect/referral (client)' },
  { event: 'client.activated', icon: '🟢', es: 'Cliente pasa a activo', en: 'Client becomes active' },
  // Lo que CONTESTA el cliente (Lissette 2026-09-08). Se auto-encienden con
  // su propio canal la primera vez que pasan. La linea nunca lleva el texto
  // del SMS, del correo ni el comentario: eso se lee en la ficha.
  { event: 'sms.inbound', icon: '💬', es: 'Llega un SMS de un cliente', en: 'Inbound SMS from a client' },
  { event: 'email.reply', icon: '📥', es: 'Un cliente contesta un correo', en: 'A client replies to an email' },
  { event: 'renewal.review.responded', icon: '🔁', es: 'Contestan el link de renovación', en: 'Renewal link answered' },
  // UN solo evento para los tres sitios (renovación, prospecto, referido):
  // lo que se atiende es la misma tarea. Cada línea dice de cuál viene.
  // Se auto-enciende con su canal "Lista para someter" la primera vez.
  { event: 'ready.to_submit', icon: '📤', es: 'Lista para someter (renovación, prospecto o referido)', en: 'Ready to submit (renewal, prospect or referral)' },
  // Paula Angarita 2026-10-06: el equipo ve las citas médicas y quién se
  // encargó. Se auto-enciende con su canal "Citas médicas" la primera vez.
  // La línea nunca lleva médico, especialidad ni razón (dato clínico).
  { event: 'medical.appointment', icon: '🩺', es: 'Cita médica nueva o cambio de estatus (y quién)', en: 'Medical appointment created or status changed (and by whom)' },
];

// Render a message body with clickable links — http(s):// and www. URLs open
// in a new tab; the rest stays plain text (React escapes it, no HTML risk).
// Single capturing group → odd indices of split() are the URL matches.
const URL_RE = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;
function linkify(body) {
  const s = String(body || '');
  if (!/https?:\/\/|www\./i.test(s)) return s;
  return s.split(URL_RE).map((part, i) => (
    i % 2 === 1
      // color:'inherit' — my own bubble is CYAN, so a cyan link was invisible
      // there (Lissette 2026-07-16). Inheriting keeps it dark-on-cyan in my
      // bubbles and light-on-dark in teammates', with underline + bold as the
      // link signal.
      ? <a key={i} href={part.startsWith('www.') ? `https://${part}` : part} target="_blank" rel="noopener noreferrer"
          style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline', wordBreak: 'break-all' }}>{part}</a>
      : part
  ));
}

const PALETTE = ['#00d4ff', '#7c5cff', '#ff8a00', '#19c37d', '#ff5d8f', '#f4c430', '#5ec8ff', '#b069ff'];
function colorFor(id) {
  const s = String(id || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
}
function authorName(a) {
  if (!a) return 'Unknown';
  return a.seatLabel || a.displayName || a.email || 'Unknown';
}
// Round avatar: shows the user's uploaded photo when present, otherwise the
// initials-on-color circle. Used for messages, the DM picker and DM headers.
function Avatar({ photo, name, color, size = 30, fontSize = '.7rem' }) {
  const c = color || '#00d4ff';
  if (photo) {
    return (
      <img src={photo} alt={name || ''} title={name || ''}
        style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, objectFit: 'cover', display: 'block', background: 'var(--c-surface2,#112538)' }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: c + '22', color: c, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, fontWeight: 700 }}>
      {initials(name)}
    </div>
  );
}
// Green "online" dot, overlaid on an avatar's bottom-right corner.
function OnlineDot({ size = 9 }) {
  return (
    <span style={{
      position: 'absolute', right: -1, bottom: -1, width: size, height: size,
      borderRadius: '50%', background: '#19c37d',
      border: '2px solid var(--c-surface,#0c1c2e)', boxSizing: 'content-box',
    }} />
  );
}
function fmtTime(at) {
  try { return new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}
function fmtDateTime(at) {
  try { return new Date(at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) + ' ' + fmtTime(at); }
  catch { return ''; }
}
function dayKey(at) {
  const d = new Date(at);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function dayLabel(at, lang) {
  const d = new Date(at);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (dayKey(at) === dayKey(today)) return lang === 'es' ? 'Hoy' : 'Today';
  if (dayKey(at) === dayKey(yest)) return lang === 'es' ? 'Ayer' : 'Yesterday';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}
function channelTitle(ch, es) {
  if (!ch) return '';
  if (ch.type === 'general') return es ? 'General' : 'General';
  return ch.name;
}

// pushManager.subscribe() wants the VAPID public key as a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function TeamChatView({ currentUser, lang = 'en', embedded = false, onNavigate }) {
  const es = lang === 'es';
  const meId = currentUser?.id;

  const [channels, setChannels] = useState([{ key: GENERAL, name: 'General', type: 'general', unread: 0, lastMessageAt: null }]);
  const [activeKey, setActiveKey] = useState(GENERAL);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelBusy, setNewChannelBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  // Pending attachments (images or documents), sent with the next message.
  // Each: { dataUrl, name, mime, isImage }. Multiple files → the first goes
  // out with the typed text, the rest as follow-up messages (one attachment
  // per message, matching the single-attachment DB shape).
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const MAX_PENDING = 4;
  // Mirrors the backend whitelist (chat.routes persistChatFile): images
  // (no SVG) + PDF / Word / Excel / PowerPoint / TXT / CSV / ZIP, ≤ 5 MB.
  const DOC_EXT_RE = /\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip)$/i;
  const fileAllowed = (f) =>
    (f.type.startsWith('image/') && f.type !== 'image/svg+xml') || DOC_EXT_RE.test(f.name || '');
  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    let rejected = null;
    for (const f of files) {
      if (!fileAllowed(f)) { rejected = es ? `Tipo no permitido: ${f.name}` : `Type not allowed: ${f.name}`; continue; }
      if (f.size > 5_000_000) { rejected = es ? `${f.name} supera 5 MB` : `${f.name} exceeds 5 MB`; continue; }
      const reader = new FileReader();
      reader.onload = () => setPendingFiles((prev) => (
        prev.length >= MAX_PENDING
          ? prev
          : [...prev, { dataUrl: reader.result, name: f.name, mime: f.type, isImage: f.type.startsWith('image/') }]
      ));
      reader.readAsDataURL(f);
    }
    setError(rejected || '');
  };
  const [typingNames, setTypingNames] = useState([]); // names typing in active channel

  // Pro pack state.
  const [onlineIds, setOnlineIds] = useState(() => new Set()); // presence
  const [reactFor, setReactFor] = useState(null); // message id with open emoji picker
  const [thread, setThread] = useState(null); // { loading, parent, replies }
  const [threadText, setThreadText] = useState('');
  const [threadSending, setThreadSending] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = panel closed
  const [searchMeta, setSearchMeta] = useState({});
  const [highlightId, setHighlightId] = useState(null); // flash after a search jump
  // Push notifications: unavailable | off | on | denied | busy
  const [notif, setNotif] = useState('unavailable');
  // Automatic feed rules modal (owners only).
  const [showFeedCfg, setShowFeedCfg] = useState(false);
  const [feedRules, setFeedRules] = useState(null); // null = loading
  const [feedSaving, setFeedSaving] = useState(false);

  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const activeKeyRef = useRef(activeKey);
  useEffect(() => { activeKeyRef.current = activeKey; }, [activeKey]);
  const threadRef = useRef(thread);
  useEffect(() => { threadRef.current = thread; }, [thread]);
  const lastTypingSentRef = useRef(0);
  const typingTimersRef = useRef({}); // userId -> timeout to clear

  const activeChannel = channels.find((c) => c.key === activeKey) || channels[0];

  // ── Loading helpers ──────────────────────────────────────────────────
  const loadChannels = async () => {
    try {
      const rows = await listChatChannels();
      // Always keep "general" present even if the API didn't return it
      // (it does, but be defensive).
      const hasGeneral = rows.some((r) => r.key === GENERAL);
      const merged = hasGeneral ? rows : [{ key: GENERAL, name: 'General', type: 'general', unread: 0, lastMessageAt: null }, ...rows];
      // Preserve any transient DM the user just opened that has no messages
      // yet (so it isn't returned by the server-derived list).
      setChannels((prev) => {
        const serverKeys = new Set(merged.map((m) => m.key));
        const transient = prev.filter((p) => p.type === 'dm' && !serverKeys.has(p.key));
        return [...merged, ...transient];
      });
    } catch (err) {
      setError(err.message || 'Failed to load channels');
    }
  };

  const loadMessages = async (key) => {
    setLoading(true);
    try {
      const rows = await listChatMessages({ channel: key, limit: PAGE });
      setMessages(rows);
      setHasMore(rows.length >= PAGE);
      markChatRead({ channel: key }).catch(() => {});
      setChannels((prev) => prev.map((c) => (c.key === key ? { ...c, unread: 0 } : c)));
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadChannels();
      try {
        const { users } = await listUsers();
        if (!cancelled) setMembers((users || []).filter((u) => u.id !== meId && u.isActive !== false));
      } catch { /* members are best-effort (DM picker) */ }
      try {
        const p = await getChatPresence();
        if (!cancelled) setOnlineIds(new Set(p.online || []));
      } catch { /* presence is best-effort */ }
      if (!cancelled) await loadMessages(GENERAL);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read receipts ("Leído") — other participants' lastReadAt for the ACTIVE
  // channel, keyed by userId (ms). A ✓✓ shows on my own messages once someone
  // else's lastReadAt passes the message time; live updates arrive via the
  // chat.read SSE event (Lissette 2026-07-16).
  const [readStates, setReadStates] = useState({});
  useEffect(() => {
    let alive = true;
    setReadStates({});
    listChatReadStates(activeKey)
      .then((rows) => {
        if (!alive) return;
        setReadStates(Object.fromEntries((rows || []).map((r) => [r.userId, +new Date(r.lastReadAt)])));
      })
      .catch(() => { /* receipts are best-effort */ });
    return () => { alive = false; };
  }, [activeKey]);
  const membersById = useMemo(() => {
    const map = {};
    for (const u of members) map[u.id] = u.seatLabel || u.displayName || u.name || '';
    return map;
  }, [members]);

  // Push notifications: reflect this browser's actual state AND self-heal.
  // The user's choice is remembered in localStorage — browsers sometimes
  // drop the push subscription (profile cleanup, SW update, PWA vs tab),
  // which used to flip the bell back to "off" on every re-login (Lissette
  // 2026-07-08: "activo la campanita, salgo y entro y se vuelve a
  // desactivar"). If the user turned it on and permission is still granted,
  // silently re-subscribe instead of forgetting their choice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        // No SW registration (e.g. vite dev) → push can't work here.
        if (!reg) return;
        if (Notification.permission === 'denied') { if (!cancelled) setNotif('denied'); return; }
        let sub = await reg.pushManager.getSubscription();
        let wanted = false;
        try { wanted = localStorage.getItem('deskte:chatPush') === '1'; } catch { /* private mode */ }
        if (!sub && wanted && Notification.permission === 'granted') {
          // Re-subscribe silently — no user gesture needed once granted.
          try {
            const { enabled, key } = await getPushPublicKey();
            if (enabled && key) {
              sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(key),
              });
              await subscribePush(sub.toJSON());
            }
          } catch { /* fall through to 'off' */ }
        }
        if (cancelled) return;
        setNotif(sub ? 'on' : 'off');
      } catch { /* keep 'unavailable' */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply an aggregated reaction list to a message wherever it lives
  // (main list, thread parent, thread replies).
  const applyReactions = (id, reactions) => {
    setMessages((prev) => prev.map((x) => (x.id === id ? { ...x, reactions } : x)));
    setThread((prev) => {
      if (!prev || prev.loading) return prev;
      let changed = false;
      let parent = prev.parent;
      if (parent && parent.id === id) { parent = { ...parent, reactions }; changed = true; }
      const replies = prev.replies.map((r) => {
        if (r.id !== id) return r;
        changed = true;
        return { ...r, reactions };
      });
      return changed ? { ...prev, parent, replies } : prev;
    });
  };

  // Live updates from the App-level SSE handler.
  useEffect(() => {
    const onMsg = (e) => {
      const m = e?.detail;
      if (!m || !m.id) return;
      const ch = m.channel || GENERAL;

      // Thread reply: never enters the top-level list. Bump the parent's
      // counter, append to an open matching thread panel, and count unread
      // for inactive channels like any other message.
      if (m.parentId) {
        if (ch === activeKeyRef.current) {
          setMessages((prev) => prev.map((x) => (x.id === m.parentId
            ? { ...x, replyCount: (x.replyCount || 0) + 1, lastReplyAt: m.createdAt }
            : x)));
          markChatRead({ channel: ch }).catch(() => {});
          setChannels((prev) => prev.map((c) => (c.key === ch ? { ...c, lastMessageAt: m.createdAt, unread: 0 } : c)));
        } else {
          setChannels((prev) => {
            const known = prev.some((c) => c.key === ch);
            if (!known) { loadChannels(); return prev; }
            const mine = m.author?.id === meId;
            return prev.map((c) => (c.key === ch ? { ...c, lastMessageAt: m.createdAt, unread: mine ? c.unread : (c.unread || 0) + 1 } : c));
          });
        }
        setThread((prev) => {
          if (!prev || prev.loading || prev.parent?.id !== m.parentId) return prev;
          if (prev.replies.some((r) => r.id === m.id)) return prev;
          return {
            ...prev,
            replies: [...prev.replies, m],
            parent: { ...prev.parent, replyCount: (prev.parent.replyCount || 0) + 1 },
          };
        });
        return;
      }

      if (ch === activeKeyRef.current) {
        setMessages((prev) => {
          if (prev.some((x) => x.id === m.id)) return prev;
          const next = [...prev, m];
          next.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          return next;
        });
        markChatRead({ channel: ch }).catch(() => {});
        setChannels((prev) => prev.map((c) => (c.key === ch ? { ...c, lastMessageAt: m.createdAt, unread: 0 } : c)));
      } else {
        // Bump unread on the matching channel; if we don't know it yet
        // (e.g. a brand-new DM directed at us), reload the channel list.
        setChannels((prev) => {
          const known = prev.some((c) => c.key === ch);
          if (!known) { loadChannels(); return prev; }
          const mine = m.author?.id === meId;
          return prev.map((c) => (c.key === ch ? { ...c, lastMessageAt: m.createdAt, unread: mine ? c.unread : (c.unread || 0) + 1 } : c));
        });
      }
    };
    const onUpdated = (e) => {
      const m = e?.detail;
      if (!m || !m.id) return;
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...m } : x)));
      setThread((prev) => {
        if (!prev || prev.loading) return prev;
        const parent = prev.parent && prev.parent.id === m.id ? { ...prev.parent, ...m } : prev.parent;
        const replies = prev.replies.map((r) => (r.id === m.id ? { ...r, ...m } : r));
        return { ...prev, parent, replies };
      });
    };
    const onDeleted = (e) => {
      const d = e?.detail;
      if (!d || !d.id) return;
      const tomb = { deleted: true, body: '', attachmentUrl: null };
      setMessages((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...tomb } : x)));
      setThread((prev) => {
        if (!prev || prev.loading) return prev;
        const parent = prev.parent && prev.parent.id === d.id ? { ...prev.parent, ...tomb } : prev.parent;
        const replies = prev.replies.map((r) => (r.id === d.id ? { ...r, ...tomb } : r));
        return { ...prev, parent, replies };
      });
    };
    const onReaction = (e) => {
      const d = e?.detail;
      if (!d || !d.id) return;
      applyReactions(d.id, d.reactions || []);
    };
    const onPresence = (e) => {
      const d = e?.detail;
      if (!d || !Array.isArray(d.online)) return;
      setOnlineIds(new Set(d.online));
    };
    const onTyping = (e) => {
      const t = e?.detail;
      if (!t || !t.userId || t.userId === meId) return;
      if ((t.channel || GENERAL) !== activeKeyRef.current) return;
      const name = t.displayName || (es ? 'Alguien' : 'Someone');
      setTypingNames((prev) => (prev.includes(name) ? prev : [...prev, name]));
      clearTimeout(typingTimersRef.current[t.userId]);
      typingTimersRef.current[t.userId] = setTimeout(() => {
        setTypingNames((prev) => prev.filter((n) => n !== name));
      }, 4000);
    };
    const onChannel = () => { loadChannels(); };
    // A teammate marked a channel read → refresh their ✓✓ marker live.
    const onRead = (e) => {
      const d = e?.detail;
      if (!d || !d.userId || d.userId === meId) return;
      if ((d.channel || GENERAL) !== activeKeyRef.current) return;
      setReadStates((prev) => ({ ...prev, [d.userId]: +new Date(d.lastReadAt || Date.now()) }));
    };
    window.addEventListener('deskte:chat-read', onRead);
    window.addEventListener('deskte:chat-message', onMsg);
    window.addEventListener('deskte:chat-message-updated', onUpdated);
    window.addEventListener('deskte:chat-message-deleted', onDeleted);
    window.addEventListener('deskte:chat-reaction', onReaction);
    window.addEventListener('deskte:chat-presence', onPresence);
    window.addEventListener('deskte:chat-typing', onTyping);
    window.addEventListener('deskte:chat-channel', onChannel);
    return () => {
      window.removeEventListener('deskte:chat-message', onMsg);
      window.removeEventListener('deskte:chat-message-updated', onUpdated);
      window.removeEventListener('deskte:chat-message-deleted', onDeleted);
      window.removeEventListener('deskte:chat-reaction', onReaction);
      window.removeEventListener('deskte:chat-presence', onPresence);
      window.removeEventListener('deskte:chat-typing', onTyping);
      window.removeEventListener('deskte:chat-channel', onChannel);
      window.removeEventListener('deskte:chat-read', onRead);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId]);

  // Auto-scroll to newest on growth — scroll ONLY the messages container, never
  // the page. The previous bottomRef.scrollIntoView() walked up and scrolled
  // every scrollable ancestor (the main content area too), yanking the whole
  // page down on open and on each new message (Lissette 2026-06-13). Setting
  // scrollTop on the list container keeps the page anchored where it was.
  useEffect(() => {
    if (loadingOlder) return;
    if (highlightId) return; // a search jump positions itself
    const el = scrollRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [messages.length, activeKey, thread?.replies?.length]);

  // After a search jump, flash the target briefly, then release.
  useEffect(() => {
    if (!highlightId) return;
    const el = scrollRef.current;
    if (el) {
      requestAnimationFrame(() => {
        const node = el.querySelector(`[data-msg-id="${highlightId}"]`);
        if (node) node.scrollIntoView({ block: 'center' });
      });
    }
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightId]);

  // ── Actions ──────────────────────────────────────────────────────────
  const selectChannel = (key) => {
    if (key === activeKey) return;
    setActiveKey(key);
    setError('');
    setMessages([]);
    setTypingNames([]);
    setEditingId(null);
    setPendingFiles([]);
    setThread(null);
    setReactFor(null);
    setHighlightId(null);
    loadMessages(key);
  };

  const loadOlder = async () => {
    if (!messages.length || loadingOlder) return;
    setLoadingOlder(true);
    const before = messages[0].createdAt;
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const older = await listChatMessages({ channel: activeKey, limit: PAGE, before });
      setMessages((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        return [...older.filter((o) => !ids.has(o.id)), ...prev];
      });
      setHasMore(older.length >= PAGE);
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevHeight; });
    } catch (err) {
      setError(err.message || 'Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  const send = async () => {
    const body = text.trim();
    if ((!body && !pendingFiles.length) || sending) return;
    setSending(true);
    setError('');
    try {
      // One attachment per message (DB shape): the text rides with the first
      // file; extra files go out as follow-up messages, in order.
      const batches = pendingFiles.length
        ? pendingFiles.map((f, i) => ({ body: i === 0 ? body : '', attachment: { dataUrl: f.dataUrl, name: f.name } }))
        : [{ body, attachment: undefined }];
      let last = null;
      for (const b of batches) {
        const saved = await sendChatMessage({ channel: activeKey, body: b.body, attachment: b.attachment });
        last = saved;
        setMessages((prev) => (prev.some((x) => x.id === saved.id) ? prev : [...prev, saved]));
      }
      setText('');
      setPendingFiles([]);
      if (last) setChannels((prev) => prev.map((c) => (c.key === activeKey ? { ...c, lastMessageAt: last.createdAt } : c)));
    } catch (err) {
      setError(err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // Throttled "is typing…" ping — at most once every 2.5s while typing.
  const pingTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2500) {
      lastTypingSentRef.current = now;
      sendChatTyping(activeKey);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const onPickFiles = (e) => {
    addFiles(e.target.files);
    if (e.target) e.target.value = '';
  };
  // Ctrl+V a screenshot (or copied file) straight into the composer.
  const onComposerPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); addFiles(files); }
  };
  // Drag & drop onto the message list or the composer.
  const dropProps = {
    onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); },
  };

  const saveEdit = async (id) => {
    const body = editText.trim();
    if (!body) return;
    try {
      const saved = await editChatMessage(id, body);
      setMessages((prev) => prev.map((x) => (x.id === id ? { ...x, ...saved } : x)));
      setEditingId(null);
      setEditText('');
    } catch (err) {
      setError(err.message || 'Failed to edit');
    }
  };

  const removeMessage = async (id) => {
    const ok = window.confirm(es ? '¿Eliminar este mensaje?' : 'Delete this message?');
    if (!ok) return;
    try {
      await deleteChatMessage(id);
      const tomb = { deleted: true, body: '', attachmentUrl: null };
      setMessages((prev) => prev.map((x) => (x.id === id ? { ...x, ...tomb } : x)));
      setThread((prev) => {
        if (!prev || prev.loading) return prev;
        const parent = prev.parent && prev.parent.id === id ? { ...prev.parent, ...tomb } : prev.parent;
        const replies = prev.replies.map((r) => (r.id === id ? { ...r, ...tomb } : r));
        return { ...prev, parent, replies };
      });
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  const createChannel = async () => {
    const name = newChannelName.trim();
    if (!name || newChannelBusy) return;
    setNewChannelBusy(true);
    try {
      const ch = await createChatChannel({ name });
      setShowNewChannel(false);
      setNewChannelName('');
      await loadChannels();
      selectChannel(ch.key);
    } catch (err) {
      setError(err.message || 'Failed to create channel');
    } finally {
      setNewChannelBusy(false);
    }
  };

  const startDm = async (userId) => {
    setShowNewDm(false);
    try {
      const dm = await resolveDmChannel(userId);
      setChannels((prev) => (prev.some((c) => c.key === dm.key)
        ? prev
        : [...prev, { ...dm, unread: 0, lastMessageAt: null }]));
      selectChannel(dm.key);
    } catch (err) {
      setError(err.message || 'Failed to open conversation');
    }
  };

  // Archive (soft-delete) a topic channel: it disappears from everyone's
  // sidebar but its messages are kept (HIPAA history). The backend allows
  // the creator or an agency owner; feed rules pointing here fall back to
  // "general" automatically.
  const archiveChannel = async (ch) => {
    if (!ch?.id) return;
    const ok = window.confirm(es
      ? `¿Eliminar el canal "${ch.name}"? Los mensajes se conservan, pero el canal deja de mostrarse para todos.`
      : `Delete channel "${ch.name}"? Messages are kept, but the channel is hidden for everyone.`);
    if (!ok) return;
    try {
      await archiveChatChannel(ch.id);
      setChannels((prev) => prev.filter((c) => c.key !== ch.key));
      if (activeKeyRef.current === ch.key) selectChannel(GENERAL);
    } catch (err) {
      setError(err.message || 'Failed to delete channel');
    }
  };

  // ── Reactions ────────────────────────────────────────────────────────
  const doToggleReaction = async (id, emoji) => {
    setReactFor(null);
    try {
      const d = await toggleChatReaction(id, emoji);
      applyReactions(d.id, d.reactions || []);
    } catch (err) {
      setError(err.message || 'Failed to react');
    }
  };

  // ── Threads ──────────────────────────────────────────────────────────
  const openThread = async (anchorId) => {
    setReactFor(null);
    setThread({ loading: true, parent: null, replies: [] });
    try {
      const d = await getChatThread(anchorId);
      setThread({ loading: false, parent: d.parent, replies: d.replies || [] });
    } catch (err) {
      setThread(null);
      setError(err.message || 'Failed to open thread');
    }
  };

  const sendReply = async () => {
    const body = threadText.trim();
    const parent = threadRef.current?.parent;
    if (!body || threadSending || !parent) return;
    setThreadSending(true);
    try {
      const saved = await sendChatMessage({ channel: activeKey, body, parentId: parent.id });
      setThread((prev) => {
        if (!prev || prev.loading || prev.parent?.id !== parent.id) return prev;
        if (prev.replies.some((r) => r.id === saved.id)) return prev;
        return {
          ...prev,
          replies: [...prev.replies, saved],
          parent: { ...prev.parent, replyCount: (prev.parent.replyCount || 0) + 1 },
        };
      });
      setMessages((prev) => prev.map((x) => (x.id === parent.id
        ? { ...x, replyCount: (x.replyCount || 0) + 1, lastReplyAt: saved.createdAt }
        : x)));
      setThreadText('');
    } catch (err) {
      setError(err.message || 'Failed to reply');
    } finally {
      setThreadSending(false);
    }
  };

  // ── Search ───────────────────────────────────────────────────────────
  const runSearch = async () => {
    const q = searchQ.trim();
    if (q.length < 2 || searchBusy) return;
    setSearchBusy(true);
    setError('');
    try {
      const { results, meta } = await searchChatMessages({ q });
      setSearchResults(results);
      setSearchMeta(meta || {});
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setSearchBusy(false);
    }
  };

  const jumpTo = async (r) => {
    const targetCh = r.channel || GENERAL;
    setSearchOpen(false);
    setSearchResults(null);
    setThread(null);
    setReactFor(null);
    if (targetCh !== activeKeyRef.current) {
      setActiveKey(targetCh);
      setMessages([]);
      setTypingNames([]);
      // A DM hit might not be in the sidebar yet (no recent messages) —
      // add a transient row so the header renders something sensible.
      setChannels((prev) => (prev.some((c) => c.key === targetCh)
        ? prev
        : [...prev, {
            key: targetCh,
            name: targetCh.startsWith('dm:') ? (es ? 'Mensaje directo' : 'Direct message') : targetCh,
            type: targetCh.startsWith('dm:') ? 'dm' : 'topic',
            unread: 0, lastMessageAt: null,
          }]));
    }
    setLoading(true);
    try {
      if (r.parentId) {
        // The hit is a thread reply: land on the channel, then open the
        // parent's thread panel where the reply actually lives.
        const rows = await listChatMessages({ channel: targetCh, limit: PAGE });
        setMessages(rows);
        setHasMore(rows.length >= PAGE);
        await openThread(r.parentId);
      } else {
        // Load the page of history that ENDS at the hit so it's on screen.
        const before = new Date(new Date(r.createdAt).getTime() + 1).toISOString();
        const rows = await listChatMessages({ channel: targetCh, limit: PAGE, before });
        setMessages(rows);
        setHasMore(rows.length >= PAGE);
        setHighlightId(r.id);
      }
      markChatRead({ channel: targetCh }).catch(() => {});
      setChannels((prev) => prev.map((c) => (c.key === targetCh ? { ...c, unread: 0 } : c)));
    } catch (err) {
      setError(err.message || 'Failed to open result');
    } finally {
      setLoading(false);
    }
  };

  // ── Push notifications ───────────────────────────────────────────────
  const enableNotifs = async () => {
    if (notif === 'busy') return;
    setNotif('busy');
    setError('');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setNotif('denied'); return; }
      const { enabled, key } = await getPushPublicKey();
      if (!enabled || !key) {
        setNotif('off');
        setError(es ? 'Las notificaciones no están configuradas en el servidor todavía.' : 'Push is not configured on the server yet.');
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        setNotif('off');
        setError(es ? 'Disponible solo en la app publicada (no en desarrollo).' : 'Only available in the deployed app.');
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
      await subscribePush(sub.toJSON());
      // Remember the CHOICE (not just the subscription) so the bell
      // self-heals on the next visit even if the browser drops the sub.
      try { localStorage.setItem('deskte:chatPush', '1'); } catch { /* private mode */ }
      setNotif('on');
    } catch (err) {
      setNotif('off');
      setError(err.message || 'Failed to enable notifications');
    }
  };

  const disableNotifs = async () => {
    if (notif === 'busy') return;
    setNotif('busy');
    try { localStorage.removeItem('deskte:chatPush'); } catch { /* private mode */ }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await unsubscribePush(sub.endpoint).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      setNotif('off');
    } catch {
      setNotif('off');
    }
  };

  // ── Automatic feed rules (Deskte bot) ────────────────────────────────
  const roleLc = String(currentUser?.role || '').toLowerCase();
  // Seats (frontend role "assistant", backend SEAT) can now configure the
  // automatic CRM notices too — the backend PUT /event-rules allows SEAT to
  // save (Lissette 2026-07-14). Keep 'seat' as a defensive alias in case the
  // raw enum ever reaches here un-mapped.
  const canConfigFeed = roleLc === 'agency_owner' || roleLc === 'solo_agent' || roleLc === 'platform_owner' || roleLc === 'assistant' || roleLc === 'seat';

  const openFeedCfg = async () => {
    setShowFeedCfg(true);
    setFeedRules(null);
    try {
      const rules = await getChatEventRules();
      setFeedRules(rules);
    } catch (err) {
      setShowFeedCfg(false);
      setError(err.message || 'Failed to load settings');
    }
  };

  const patchFeedRule = (event, patch) => {
    setFeedRules((prev) => {
      const filas = prev || [];
      if (filas.some((r) => r.event === event)) {
        return filas.map((r) => (r.event === event ? { ...r, ...patch } : r));
      }
      // El servidor no devolvió fila para este aviso, así que `map` no tenía
      // nada que cambiar: la casilla se quedaba sin marcar y no había ni un
      // error. Pasa en la ventana del despliegue —el frontend sale unos
      // minutos ANTES que el backend, así que la pantalla nueva ofrece un
      // aviso que el servidor viejo todavía no lista— y duraría hasta que
      // alguien recargara después. Se añade la fila aquí.
      return [...filas, { event, enabled: false, channelKey: GENERAL, ...patch }];
    });
  };

  const saveFeedCfg = async () => {
    if (!feedRules || feedSaving) return;
    setFeedSaving(true);
    try {
      const saved = await saveChatEventRules(feedRules.map(({ event, enabled, channelKey }) => ({ event, enabled, channelKey })));
      setFeedRules(saved);
      setShowFeedCfg(false);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setFeedSaving(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────
  // El canal propio de cada quien: lleva SU nombre y solo lo ve esa persona
  // (Lissette 2026-08-20: "un canal fijo que diga por ejemplo haiffa"). El
  // servidor lo manda siempre, tenga mensajes o no, para que se sepa dónde
  // van a caer las cosas antes de que caiga la primera.
  const personalChannel = channels.find((c) => c.type === 'deskte');
  const topicChannels = channels.filter((c) => c.type === 'general' || c.type === 'topic');
  const dmChannels = channels
    .filter((c) => c.type === 'dm')
    .sort((a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0));

  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const k = dayKey(m.createdAt);
      if (k !== lastDay) { out.push({ type: 'day', key: 'day-' + k, at: m.createdAt }); lastDay = k; }
      out.push({ type: 'msg', key: m.id, m });
    }
    return out;
  }, [messages]);

  const channelLabelFor = (key) => {
    if (key === GENERAL) return es ? 'General' : 'General';
    const ch = channels.find((c) => c.key === key);
    if (ch) return channelTitle(ch, es);
    return key.startsWith('dm:') ? (es ? 'Mensaje directo' : 'Direct message') : `#${key}`;
  };

  // ── Sub-render: reaction chips + quick picker ────────────────────────
  const ReactionChips = ({ m, mine }) => {
    const list = m.reactions || [];
    if (!list.length) return null;
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
        {list.map((rx) => {
          const iReacted = (rx.userIds || []).includes(meId);
          return (
            <button
              key={rx.emoji}
              onClick={() => doToggleReaction(m.id, rx.emoji)}
              title={(rx.names || []).filter(Boolean).join(', ')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                background: iReacted ? 'var(--c-cyan-dim,rgba(0,212,255,.16))' : 'var(--c-surface2,#112538)',
                border: iReacted ? '1px solid var(--c-cyan,#00d4ff)' : '1px solid var(--c-border,#1a3550)',
                borderRadius: 999, padding: '.1rem .45rem', fontSize: '.74rem',
                color: iReacted ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)', fontWeight: 700,
              }}
            >
              <span style={{ fontSize: '.85rem', lineHeight: 1 }}>{rx.emoji}</span>{rx.count}
            </button>
          );
        })}
      </div>
    );
  };

  const EmojiPicker = ({ m }) => (
    <div style={{
      position: 'absolute', bottom: '100%', marginBottom: 4, zIndex: 30,
      background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)',
      borderRadius: 10, padding: '4px 6px', display: 'flex', gap: 2, boxShadow: '0 6px 18px rgba(0,0,0,.45)',
    }}>
      {QUICK_EMOJIS.map((em) => (
        <button key={em} onClick={() => doToggleReaction(m.id, em)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', lineHeight: 1, padding: '4px 3px', borderRadius: 6 }}
          onMouseEnter={(ev) => (ev.currentTarget.style.background = 'var(--c-surface2,#112538)')}
          onMouseLeave={(ev) => (ev.currentTarget.style.background = 'none')}>
          {em}
        </button>
      ))}
    </div>
  );

  // Hover action buttons for a message bubble. Reactions for everyone;
  // reply only on top-level bubbles in the main pane; edit/delete on mine.
  const HoverActions = ({ m, mine, allowReply }) => (
    <span className="dkt-chat-actions" style={{ display: 'flex', gap: 2, opacity: 0, transition: 'opacity .15s', position: 'relative' }}>
      <button title={es ? 'Reaccionar' : 'React'} onClick={() => setReactFor((prev) => (prev === m.id ? null : m.id))}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 2 }}>
        <SmilePlus size={13} />
      </button>
      {allowReply && (
        <button title={es ? 'Responder en hilo' : 'Reply in thread'} onClick={() => openThread(m.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 2 }}>
          <Reply size={13} />
        </button>
      )}
      {mine && m.body && !m.attachmentUrl && (
        <button title={es ? 'Editar' : 'Edit'} onClick={() => { setEditingId(m.id); setEditText(m.body); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 2 }}>
          <Pencil size={12} />
        </button>
      )}
      {mine && (
        <button title={es ? 'Eliminar' : 'Delete'} onClick={() => removeMessage(m.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 2 }}>
          <Trash2 size={12} />
        </button>
      )}
    </span>
  );

  // One message bubble (shared by the main list and the thread panel).
  const MessageRow = ({ m, allowReply, showThreadLink }) => {
    // Bot feed line: no author, isSystem=true (posted by the CRM itself).
    const isBot = !!m.isSystem || (!m.deleted && !m.author);
    const mine = !isBot && m.author?.id === meId;
    const color = isBot ? '#19c37d' : colorFor(m.author?.id);
    if (m.deleted) {
      return (
        <div style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 8, margin: '4px 0' }}>
          <div style={{ maxWidth: '72%', fontSize: '.78rem', fontStyle: 'italic', color: 'var(--c-text3,#45688a)', background: 'transparent', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 10, padding: '.4rem .7rem' }}>
            {es ? 'mensaje eliminado' : 'message deleted'}
          </div>
        </div>
      );
    }
    const isEditing = editingId === m.id;
    const highlighted = highlightId === m.id;
    return (
      <div data-msg-id={m.id} className="dkt-chat-msg" style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 8, margin: '4px 0', alignItems: 'flex-end' }}>
        {isBot ? (
          <div title="Deskte" style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, background: 'rgba(25,195,125,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem' }}>🤖</div>
        ) : (
          <Avatar
            photo={mine ? (currentUser?.profilePhoto || currentUser?.avatarUrl) : (m.author?.profilePhoto || m.author?.avatarUrl)}
            name={mine ? (currentUser?.name || currentUser?.displayName || authorName(m.author)) : authorName(m.author)}
            color={color} size={30} fontSize=".7rem" />
        )}
        <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
          {!mine && (
            <div style={{ fontSize: '.68rem', fontWeight: 700, color, marginBottom: 2, padding: '0 2px' }}>{isBot ? 'Deskte' : authorName(m.author)}</div>
          )}
          {isEditing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 280, maxWidth: '100%' }}>
              <textarea
                autoFocus
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(m.id); } if (e.key === 'Escape') { setEditingId(null); } }}
                rows={2}
                style={{ resize: 'none', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-cyan,#00d4ff)', borderRadius: 8, padding: '.4rem .6rem', color: 'var(--c-white,#eef6ff)', fontSize: '.82rem', outline: 'none', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button onClick={() => setEditingId(null)} style={{ background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', fontSize: '.72rem', cursor: 'pointer' }}>{es ? 'Cancelar' : 'Cancel'}</button>
                <button onClick={() => saveEdit(m.id)} style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', color: '#06283d', fontSize: '.72rem', fontWeight: 700, borderRadius: 6, padding: '.25rem .6rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={12} />{es ? 'Guardar' : 'Save'}</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexDirection: mine ? 'row-reverse' : 'row', position: 'relative' }}>
              {reactFor === m.id && <EmojiPicker m={m} />}
              {/* Mi mensaje: fondo suave del color del tema y la letra normal del
                  tema. Antes era el color lleno con letra azul oscuro fija, y en
                  el tema claro (acento azul oscuro) no se leia (Lissette
                  2026-10-22). Asi se lee en todos los temas y paletas. */}
              <div style={{
                background: mine ? 'color-mix(in srgb, var(--c-cyan,#00d4ff) 16%, var(--c-surface,#0d1c2e))' : 'var(--c-surface2,#112538)',
                color: 'var(--c-white,#eef6ff)',
                border: mine ? '1px solid color-mix(in srgb, var(--c-cyan,#00d4ff) 45%, transparent)' : '1px solid var(--c-border,#1a3550)',
                outline: highlighted ? '2px solid var(--c-cyan,#00d4ff)' : 'none',
                outlineOffset: 2,
                borderRadius: 12, borderBottomRightRadius: mine ? 3 : 12, borderBottomLeftRadius: mine ? 12 : 3,
                padding: m.attachmentUrl ? '.35rem' : '.5rem .7rem', fontSize: '.85rem', lineHeight: 1.4,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {m.attachmentUrl && (String(m.attachmentMime || '').startsWith('image/') ? (
                  <BlobLink href={m.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                    <BlobImg src={m.attachmentUrl} alt={m.attachmentName || 'image'} style={{ maxWidth: 240, maxHeight: 240, borderRadius: 8, display: 'block' }} />
                  </BlobLink>
                ) : (
                  /* Document attachment → filename chip that opens/downloads in a new tab */
                  <BlobLink href={m.attachmentUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '.45rem .6rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.78rem', fontWeight: 600, textDecoration: 'none', maxWidth: 260 }}>
                    <FileText size={16} style={{ flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.attachmentName || (es ? 'Documento' : 'Document')}</span>
                  </BlobLink>
                ))}
                {m.body && <div style={{ padding: m.attachmentUrl ? '.3rem .3rem 0' : 0 }}>{linkify(m.body)}</div>}
              </div>
              <HoverActions m={m} mine={mine} allowReply={allowReply} />
            </div>
          )}
          <ReactionChips m={m} mine={mine} />
          {isBot && m.linkTo && typeof onNavigate === 'function' && (
            <button
              onClick={() => onNavigate(m.linkTo)}
              style={{ background: 'var(--c-cyan-dim,rgba(0,212,255,.12))', border: '1px solid var(--c-cyan,#00d4ff)', cursor: 'pointer', color: 'var(--c-cyan,#00d4ff)', fontSize: '.72rem', fontWeight: 800, padding: '.2rem .6rem', borderRadius: 999, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {es ? 'Ver' : 'View'} →
            </button>
          )}
          {showThreadLink && (m.replyCount || 0) > 0 && (
            <button onClick={() => openThread(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-cyan,#00d4ff)', fontSize: '.7rem', fontWeight: 700, padding: '2px 2px 0', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MessageSquare size={11} />
              {m.replyCount} {es ? (m.replyCount === 1 ? 'respuesta' : 'respuestas') : (m.replyCount === 1 ? 'reply' : 'replies')}
            </button>
          )}
          <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', marginTop: 2, padding: '0 2px' }}>
            {fmtTime(m.createdAt)}{m.editedAt ? (es ? ' · editado' : ' · edited') : ''}
            {/* Read receipt on my own messages: gray ✓ = sent; cyan ✓✓ = read.
                DM → "Leído"; channel → how many teammates have read it. */}
            {mine && (() => {
              const t = +new Date(m.createdAt);
              const readers = Object.entries(readStates).filter(([, ts]) => ts >= t);
              if (!readers.length) return <span title={es ? 'Enviado' : 'Sent'} style={{ marginLeft: 5 }}>✓</span>;
              const isDm = String(activeKey).startsWith('dm:');
              const names = readers.map(([uid]) => membersById[uid]).filter(Boolean).join(', ');
              return (
                <span
                  title={names ? (es ? `Leído por ${names}` : `Read by ${names}`) : (es ? 'Leído' : 'Read')}
                  style={{ marginLeft: 5, color: 'var(--c-cyan,#00d4ff)', fontWeight: 700 }}
                >
                  ✓✓ {isDm ? (es ? 'Leído' : 'Read') : readers.length}
                </span>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  // ── Sub-render: a conversation-list row ──────────────────────────────
  const ChannelRow = ({ ch }) => {
    const active = ch.key === activeKey;
    const isDm = ch.type === 'dm';
    const isMine = ch.type === 'deskte';
    const color = colorFor(ch.otherUserId || ch.key);
    const online = isDm && ch.otherUserId && onlineIds.has(ch.otherUserId);
    return (
      <button
        onClick={() => selectChannel(ch.key)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '.5rem .6rem',
          borderRadius: 8, border: 'none', cursor: 'pointer', textAlign: 'left',
          background: active ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : 'transparent',
          color: active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)',
          fontWeight: ch.unread > 0 ? 700 : 500, fontSize: '.82rem', marginBottom: 1,
        }}
      >
        {isDm ? (
          <span style={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
            <Avatar photo={ch.otherPhoto} name={ch.name} color={color} size={22} fontSize=".6rem" />
            {online && <OnlineDot size={7} />}
          </span>
        ) : isMine ? (
          <Bell size={15} style={{ flexShrink: 0, color: 'var(--c-amber,#ffb700)' }} />
        ) : (
          <Hash size={15} style={{ flexShrink: 0, opacity: 0.8 }} />
        )}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{channelTitle(ch, es)}</span>
        {ch.unread > 0 && (
          <span style={{ flexShrink: 0, background: 'var(--c-cyan,#00d4ff)', color: '#06283d', fontSize: '.6rem', fontWeight: 800, borderRadius: 999, padding: '.05rem .4rem', minWidth: 16, textAlign: 'center' }}>
            {ch.unread > 99 ? '99+' : ch.unread}
          </span>
        )}
        {ch.type === 'topic' && ch.id && canConfigFeed && (
          <span
            title={es ? 'Eliminar canal' : 'Delete channel'}
            onClick={(e) => { e.stopPropagation(); archiveChannel(ch); }}
            style={{ flexShrink: 0, display: 'inline-flex', padding: 2, borderRadius: 4, color: 'var(--c-text3,#45688a)', opacity: 0.45 }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#ff6b6b'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.45'; e.currentTarget.style.color = 'var(--c-text3,#45688a)'; }}
          >
            <X size={12} />
          </span>
        )}
      </button>
    );
  };

  const sectionHdr = (label, onAdd, addTitle) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 .4rem', margin: '12px 0 4px' }}>
      <span style={{ fontSize: '.6rem', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--c-text3,#45688a)' }}>{label}</span>
      {onAdd && (
        <button onClick={onAdd} title={addTitle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 2, display: 'flex' }}>
          <Plus size={14} />
        </button>
      )}
    </div>
  );

  const dmOnline = activeChannel?.type === 'dm' && activeChannel.otherUserId && onlineIds.has(activeChannel.otherUserId);

  return (
    <div style={{ display: 'flex', gap: embedded ? 8 : 12, height: embedded ? '100%' : 'calc(100vh - 130px)', minHeight: embedded ? 0 : 440 }}>
      {/* Left: conversation list */}
      <div style={{ width: 232, flexShrink: 0, display: 'flex', flexDirection: 'column', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '10px 8px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 6px' }}>
          <MessageSquare size={17} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
          <span style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.9rem', flex: 1 }}>{es ? 'Chat de Equipo' : 'Team Chat'}</span>
          {notif !== 'unavailable' && (
            <button
              onClick={notif === 'on' ? disableNotifs : enableNotifs}
              disabled={notif === 'busy' || notif === 'denied'}
              title={
                notif === 'on' ? (es ? 'Notificaciones activadas — clic para desactivar' : 'Notifications on — click to turn off')
                : notif === 'denied' ? (es ? 'Bloqueadas en el navegador (permite las notificaciones del sitio)' : 'Blocked by the browser (allow site notifications)')
                : (es ? 'Recibir notificaciones aunque Deskte esté cerrado' : 'Get notified even when Deskte is closed')
              }
              style={{ background: 'none', border: 'none', cursor: notif === 'denied' ? 'not-allowed' : 'pointer', padding: 2, display: 'flex', color: notif === 'on' ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)' }}
            >
              {notif === 'busy' ? <Loader2 size={15} className="dkt-spin" /> : notif === 'on' ? <Bell size={15} /> : <BellOff size={15} />}
            </button>
          )}
          {canConfigFeed && (
            <button
              onClick={openFeedCfg}
              title={es ? 'Avisos automáticos del CRM en el chat' : 'Automatic CRM notices in the chat'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: 'var(--c-text3,#45688a)' }}
            >
              <Settings size={15} />
            </button>
          )}
        </div>

        {personalChannel && (
          <>
            {sectionHdr(es ? 'Mis avisos' : 'My notices')}
            <ChannelRow key={personalChannel.key} ch={personalChannel} />
          </>
        )}

        {sectionHdr(es ? 'Canales' : 'Channels', () => setShowNewChannel(true), es ? 'Crear canal' : 'Create channel')}
        {topicChannels.map((ch) => <ChannelRow key={ch.key} ch={ch} />)}

        {sectionHdr(es ? 'Mensajes directos' : 'Direct messages', () => setShowNewDm(true), es ? 'Nuevo mensaje directo' : 'New direct message')}
        {dmChannels.length === 0 && (
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', padding: '2px 6px' }}>{es ? 'Sin conversaciones' : 'No conversations'}</div>
        )}
        {dmChannels.map((ch) => <ChannelRow key={ch.key} ch={ch} />)}
      </div>

      {/* Right: active conversation / thread / search results */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          {thread ? (
            <>
              <button onClick={() => setThread(null)} title={es ? 'Volver' : 'Back'}
                style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ArrowLeft size={15} />
              </button>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.98rem' }}>{es ? 'Hilo' : 'Thread'}</div>
                <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>{channelLabelFor(activeKey)}</div>
              </div>
            </>
          ) : (
            <>
              {activeChannel?.type === 'dm'
                ? (
                  <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                    <Avatar photo={activeChannel.otherPhoto} name={activeChannel.name} color={colorFor(activeChannel.otherUserId || activeChannel.key)} size={30} fontSize=".72rem" />
                    {dmOnline && <OnlineDot size={8} />}
                  </span>
                )
                : activeChannel?.type === 'deskte'
                  ? <Bell size={20} style={{ color: 'var(--c-amber,#ffb700)' }} />
                  : <Hash size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.98rem' }}>{channelTitle(activeChannel, es)}</div>
                <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>
                  {activeChannel?.type === 'dm'
                    ? (dmOnline
                        ? (es ? '🟢 En línea · cifrado' : '🟢 Online · encrypted')
                        : (es ? 'Mensaje directo · cifrado' : 'Direct message · encrypted'))
                    : activeChannel?.type === 'deskte'
                      ? (es ? 'Tus avisos · solo tú los ves' : 'Your notices · only you see them')
                      : (activeChannel?.description || (es ? 'Canal de equipo · cifrado' : 'Team channel · encrypted'))}
                </div>
              </div>
              {searchOpen ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    autoFocus
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); if (e.key === 'Escape') { setSearchOpen(false); setSearchResults(null); } }}
                    placeholder={es ? 'Buscar mensajes…' : 'Search messages…'}
                    style={{ width: embedded ? 150 : 220, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.4rem .6rem', color: 'var(--c-white,#eef6ff)', fontSize: '.78rem', outline: 'none' }}
                  />
                  <button onClick={runSearch} disabled={searchBusy || searchQ.trim().length < 2} title={es ? 'Buscar' : 'Search'}
                    style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {searchBusy ? <Loader2 size={14} className="dkt-spin" /> : <Search size={14} />}
                  </button>
                  <button onClick={() => { setSearchOpen(false); setSearchResults(null); setSearchQ(''); }} title={es ? 'Cerrar' : 'Close'}
                    style={{ background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', display: 'flex', padding: 2 }}>
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <button onClick={() => setSearchOpen(true)} title={es ? 'Buscar en el chat' : 'Search the chat'}
                  style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Search size={14} />
                </button>
              )}
            </>
          )}
        </div>

        {/* Body: search results / thread / messages */}
        {searchResults !== null ? (
          <div style={{ flex: 1, overflowY: 'auto', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: 8 }}>
              {searchResults.length === 0
                ? (es ? 'Sin resultados.' : 'No results.')
                : `${searchResults.length} ${es ? 'resultado(s)' : 'result(s)'}`}
              {searchMeta.capped ? (es ? ' · búsqueda limitada a los mensajes más recientes' : ' · limited to the most recent messages') : ''}
            </div>
            {searchResults.map((r) => (
              <button key={r.id} onClick={() => jumpTo(r)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.55rem .7rem', marginBottom: 6, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: '.68rem', fontWeight: 800, color: 'var(--c-cyan,#00d4ff)' }}>
                    {r.channel && r.channel.startsWith('dm:') ? (es ? '✉ Directo' : '✉ Direct') : `# ${channelLabelFor(r.channel)}`}
                  </span>
                  <span style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>{authorName(r.author)} · {fmtDateTime(r.createdAt)}</span>
                  {r.parentId && <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)' }}>({es ? 'en un hilo' : 'in a thread'})</span>}
                </div>
                <div style={{ fontSize: '.8rem', color: 'var(--c-white,#eef6ff)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 60, overflow: 'hidden' }}>{r.body}</div>
              </button>
            ))}
          </div>
        ) : thread ? (
          <>
            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '14px 14px 4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {thread.loading ? (
                <div style={{ margin: 'auto', color: 'var(--c-text3,#45688a)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 size={16} className="dkt-spin" /> {es ? 'Cargando…' : 'Loading…'}
                </div>
              ) : (
                <>
                  {thread.parent && <MessageRow m={thread.parent} allowReply={false} showThreadLink={false} />}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--c-border,#1a3550)' }} />
                    <span style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>
                      {(thread.parent?.replyCount || thread.replies.length) || 0} {es ? 'respuesta(s)' : 'repl(ies)'}
                    </span>
                    <div style={{ flex: 1, height: 1, background: 'var(--c-border,#1a3550)' }} />
                  </div>
                  {thread.replies.map((r) => <MessageRow key={r.id} m={r} allowReply={false} showThreadLink={false} />)}
                  {thread.replies.length === 0 && (
                    <div style={{ textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.76rem', padding: 12 }}>
                      {es ? 'Sé el primero en responder este hilo.' : 'Be the first to reply in this thread.'}
                    </div>
                  )}
                </>
              )}
            </div>
            {error && <div style={{ color: '#ff6b6b', fontSize: '.74rem', marginTop: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-end' }}>
              <textarea
                value={threadText}
                onChange={(e) => setThreadText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                rows={1}
                placeholder={es ? 'Responder en el hilo…' : 'Reply in thread…'}
                style={{ flex: 1, resize: 'none', maxHeight: 120, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.6rem .75rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none', fontFamily: 'inherit', lineHeight: 1.4 }}
              />
              <button onClick={sendReply} disabled={threadSending || !threadText.trim()}
                style={{ flexShrink: 0, height: 40, padding: '0 16px', borderRadius: 10, border: 'none', cursor: threadText.trim() && !threadSending ? 'pointer' : 'not-allowed', background: threadText.trim() && !threadSending ? 'var(--c-cyan,#00d4ff)' : 'var(--c-surface2,#112538)', color: threadText.trim() && !threadSending ? '#06283d' : 'var(--c-text3,#45688a)', fontWeight: 700, fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {threadSending ? <Loader2 size={15} className="dkt-spin" /> : <Send size={15} />}
                {es ? 'Responder' : 'Reply'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Messages — also a drag & drop target for attachments */}
            <div ref={scrollRef} {...dropProps} style={{ flex: 1, overflowY: 'auto', background: 'var(--c-surface,#0c1c2e)', border: dragOver ? '2px dashed var(--c-cyan,#00d4ff)' : '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '14px 14px 4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {loading ? (
                <div style={{ margin: 'auto', color: 'var(--c-text3,#45688a)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 size={16} className="dkt-spin" /> {es ? 'Cargando…' : 'Loading…'}
                </div>
              ) : messages.length === 0 ? (
                <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--c-text3,#45688a)', padding: 24 }}>
                  <MessageSquare size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                  <div style={{ fontSize: '.85rem' }}>{es ? 'Aún no hay mensajes.' : 'No messages yet.'}</div>
                  <div style={{ fontSize: '.75rem', marginTop: 4 }}>{es ? '¡Escribe el primero abajo!' : 'Say the first hello below!'}</div>
                </div>
              ) : (
                <>
                  {hasMore && (
                    <button onClick={loadOlder} disabled={loadingOlder} style={{ alignSelf: 'center', margin: '2px 0 10px', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7da2c8)', borderRadius: 999, padding: '.3rem .85rem', fontSize: '.74rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {loadingOlder ? <Loader2 size={12} className="dkt-spin" /> : <RefreshCw size={12} />}
                      {es ? 'Ver mensajes anteriores' : 'Load older messages'}
                    </button>
                  )}
                  {grouped.map((item) => {
                    if (item.type === 'day') {
                      return (
                        <div key={item.key} style={{ alignSelf: 'center', margin: '10px 0 6px', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', background: 'var(--c-surface2,#112538)', borderRadius: 999, padding: '.2rem .7rem', fontWeight: 600 }}>
                          {dayLabel(item.at, lang)}
                        </div>
                      );
                    }
                    return <MessageRow key={item.key} m={item.m} allowReply={true} showThreadLink={true} />;
                  })}
                  <div ref={bottomRef} />
                </>
              )}
            </div>

            {/* Typing indicator */}
            {typingNames.length > 0 && (
              <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic', marginTop: 6, height: 14 }}>
                {typingNames.length === 1
                  ? `${typingNames[0]} ${es ? 'está escribiendo…' : 'is typing…'}`
                  : `${typingNames.slice(0, 2).join(', ')} ${es ? 'están escribiendo…' : 'are typing…'}`}
              </div>
            )}

            {error && <div style={{ color: '#ff6b6b', fontSize: '.74rem', marginTop: 6 }}>{error}</div>}

            {/* Pending attachments (images + documents) */}
            {pendingFiles.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {pendingFiles.map((f, i) => (
                  <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: 6 }}>
                    {f.isImage
                      ? <img src={f.dataUrl} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                      : <FileText size={22} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }} />}
                    <span style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name || 'file'}</span>
                    <button onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))} title={es ? 'Quitar' : 'Remove'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', display: 'flex' }}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* El canal propio es un tablon de avisos, no una conversacion:
                escribe Deskte, lee la persona. El servidor tambien lo rechaza
                (403); esto evita ofrecer una caja de texto que no lleva a
                ningun sitio. */}
            {activeChannel?.type === 'deskte' ? (
              <div style={{ marginTop: 10, padding: '.7rem .85rem', borderRadius: 12, border: '1px dashed var(--c-border,#1a3550)', color: 'var(--c-text3,#45688a)', fontSize: '.74rem', textAlign: 'center' }}>
                {es
                  ? 'Aquí cae todo lo que se te asigna. No se escribe en este canal.'
                  : 'Everything assigned to you lands here. This channel is read-only.'}
              </div>
            ) : (
            <div {...dropProps} style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-end', borderRadius: 12, outline: dragOver ? '2px dashed var(--c-cyan,#00d4ff)' : 'none', outlineOffset: 3 }}>
              <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" onChange={onPickFiles} style={{ display: 'none' }} />
              <button
                onClick={() => fileRef.current && fileRef.current.click()}
                title={es ? 'Adjuntar imagen o documento (también puedes pegar o arrastrar)' : 'Attach image or document (paste or drag & drop also works)'}
                style={{ flexShrink: 0, height: 40, width: 40, borderRadius: 10, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Paperclip size={17} />
              </button>
              <textarea
                value={text}
                onChange={(e) => { setText(e.target.value); pingTyping(); }}
                onKeyDown={onKeyDown}
                onPaste={onComposerPaste}
                rows={1}
                placeholder={es ? 'Escribe un mensaje…  (Enter envía, Shift+Enter salto de línea)' : 'Type a message…  (Enter to send, Shift+Enter for newline)'}
                style={{ flex: 1, resize: 'none', maxHeight: 120, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.6rem .75rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none', fontFamily: 'inherit', lineHeight: 1.4 }}
              />
              {(() => {
                const canSend = !sending && (text.trim() || pendingFiles.length > 0);
                return (
                  <button onClick={send} disabled={!canSend} style={{ flexShrink: 0, height: 40, padding: '0 16px', borderRadius: 10, border: 'none', cursor: canSend ? 'pointer' : 'not-allowed', background: canSend ? 'var(--c-cyan,#00d4ff)' : 'var(--c-surface2,#112538)', color: canSend ? '#06283d' : 'var(--c-text3,#45688a)', fontWeight: 700, fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {sending ? <Loader2 size={15} className="dkt-spin" /> : <Send size={15} />}
                    {es ? 'Enviar' : 'Send'}
                  </button>
                );
              })()}
            </div>
            )}
          </>
        )}
      </div>

      {/* New channel modal */}
      {showNewChannel && (
        <Modal title={es ? 'Crear canal' : 'Create channel'} onClose={() => setShowNewChannel(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', fontWeight: 600 }}>{es ? 'Nombre del canal' : 'Channel name'}</label>
            <input
              autoFocus
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createChannel(); }}
              placeholder={es ? 'ej. ventas, renovaciones' : 'e.g. sales, renewals'}
              maxLength={60}
              style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.6rem .75rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button onClick={() => setShowNewChannel(false)} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, padding: '.5rem .9rem', fontSize: '.8rem', cursor: 'pointer' }}>{es ? 'Cancelar' : 'Cancel'}</button>
              <button onClick={createChannel} disabled={!newChannelName.trim() || newChannelBusy} style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', color: '#06283d', borderRadius: 8, padding: '.5rem 1rem', fontSize: '.8rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {newChannelBusy ? <Loader2 size={13} className="dkt-spin" /> : <Plus size={13} />}{es ? 'Crear' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Automatic feed rules modal (Deskte bot) */}
      {showFeedCfg && (
        <Modal title={es ? '🤖 Avisos automáticos en el chat' : '🤖 Automatic chat notices'} onClose={() => setShowFeedCfg(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
            <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.4 }}>
              {/* Aqui no se nombra a otro CRM (Lissette, 2026-09-03). El texto
                  se apoyaba en "como Monday con Slack" para explicarse, y en
                  una pantalla de Deskte eso hace publicidad de la competencia y
                  ademas no le dice nada a quien no los use. Lo que hace falta
                  entender es QUE pasa, no a que se parece. */}
              {es
                ? 'Elige qué avisos quieres y en qué canal. Cuando pase eso en el CRM, Deskte lo publica solo en el canal del equipo — sin que nadie tenga que contarlo.'
                : 'Pick which notices you want and in which channel. When it happens in the CRM, Deskte posts it in the team channel on its own — nobody has to relay it.'}
            </div>
            {feedRules === null ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-text3,#45688a)', padding: 12 }}>
                <Loader2 size={15} className="dkt-spin" /> {es ? 'Cargando…' : 'Loading…'}
              </div>
            ) : (
              FEED_EVENT_DEFS.map((def) => {
                const rule = feedRules.find((r) => r.event === def.event) || { event: def.event, enabled: false, channelKey: GENERAL };
                // Sub-opción de otro aviso (el reparto por ramo): sangrada, sin
                // desplegable de canal, y apagada mientras su aviso lo esté —
                // sin "Póliza creada" no se publica nada, así que ofrecer el
                // reparto encendido sería ofrecer algo que no puede pasar.
                if (def.debajoDe) {
                  const padre = feedRules.find((r) => r.event === def.debajoDe);
                  const vivo = !!padre?.enabled;
                  return (
                    <label key={def.event}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '-4px 0 0 22px', padding: '.4rem .6rem', borderRadius: 10, background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', cursor: vivo ? 'pointer' : 'default', opacity: vivo ? 1 : .55 }}>
                      <input
                        type="checkbox"
                        checked={!!rule.enabled}
                        disabled={!vivo}
                        onChange={(e) => patchFeedRule(def.event, { enabled: e.target.checked })}
                        style={{ accentColor: 'var(--c-cyan,#00d4ff)', flexShrink: 0, marginTop: 2 }}
                      />
                      <span style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.35 }}>
                        {def.icon} {es ? def.es : def.en}
                      </span>
                    </label>
                  );
                }
                return (
                  <div key={def.event} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.45rem .6rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer', minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={!!rule.enabled}
                        onChange={(e) => patchFeedRule(def.event, { enabled: e.target.checked })}
                        style={{ accentColor: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '.8rem', color: 'var(--c-white,#eef6ff)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {def.icon} {es ? def.es : def.en}
                      </span>
                    </label>
                    <select
                      value={rule.channelKey || GENERAL}
                      onChange={(e) => patchFeedRule(def.event, { channelKey: e.target.value })}
                      disabled={!rule.enabled}
                      style={{ background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, color: rule.enabled ? 'var(--c-white,#eef6ff)' : 'var(--c-text3,#45688a)', fontSize: '.74rem', padding: '.3rem .4rem', maxWidth: 150 }}
                    >
                      {topicChannels.map((ch) => (
                        <option key={ch.key} value={ch.key}># {channelTitle(ch, es)}</option>
                      ))}
                    </select>
                  </div>
                );
              })
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
              <button onClick={() => setShowFeedCfg(false)} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, padding: '.5rem .9rem', fontSize: '.8rem', cursor: 'pointer' }}>{es ? 'Cancelar' : 'Cancel'}</button>
              <button onClick={saveFeedCfg} disabled={feedRules === null || feedSaving} style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', color: '#06283d', borderRadius: 8, padding: '.5rem 1rem', fontSize: '.8rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {feedSaving ? <Loader2 size={13} className="dkt-spin" /> : <Check size={13} />}{es ? 'Guardar' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* New DM modal */}
      {showNewDm && (
        <Modal title={es ? 'Nuevo mensaje directo' : 'New direct message'} onClose={() => setShowNewDm(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflowY: 'auto' }}>
            {members.length === 0 && (
              <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.8rem', padding: 8 }}>
                <Users size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {es ? 'No hay otros miembros en tu agencia todavía.' : 'No other members in your agency yet.'}
              </div>
            )}
            {members.map((u) => {
              const name = u.seatLabel || u.displayName || u.email;
              const color = colorFor(u.id);
              const online = onlineIds.has(u.id);
              return (
                <button key={u.id} onClick={() => startDm(u.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem .6rem', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'var(--c-white,#eef6ff)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--c-surface2,#112538)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                  <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                    <Avatar photo={u.profilePhoto || u.avatarUrl} name={name} color={color} size={30} fontSize=".72rem" />
                    {online && <OnlineDot size={8} />}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: '.85rem', fontWeight: 600 }}>{name}</span>
                    <span style={{ display: 'block', fontSize: '.68rem', color: online ? '#19c37d' : 'var(--c-text3,#45688a)' }}>
                      {online ? (es ? 'En línea' : 'Online') : String(u.role || '').toLowerCase().replace('_', ' ')}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
}
